/**
 * Documents API Service
 * 
 * Serviço centralizado para comunicação com a API de Documentos.
 * Gerencia documentos da empresa, documentos dos usuários, categorias e dashboard.
 */

import api from './api'

// ============================================
// DASHBOARD
// ============================================
export const documentDashboardService = {
  /**
   * Busca dados do dashboard de documentação
   * Retorna documentos ausentes, prestes a vencer e estatísticas gerais
   */
  getDashboard: async () => {
    const response = await api.get('/api/documents/dashboard')
    return response.data
  }
}

// ============================================
// DOCUMENTOS DA EMPRESA
// ============================================
export const companyDocumentService = {
  /**
   * Lista documentos da empresa
   * @param {Object} filters - Filtros (category_id, expiry_soon, page, limit)
   */
  getAll: async (filters = {}) => {
    const response = await api.get('/api/documents/company', { params: filters })
    return response.data
  },

  /**
   * Busca documento da empresa por ID
   * @param {number} id - ID do documento
   */
  getById: async (id) => {
    const response = await api.get(`/api/documents/company/${id}`)
    return response.data
  },

  /**
   * Faz upload de documento da empresa
   * @param {File} file - Arquivo a ser enviado
   * @param {Object} data - Dados do documento (title, category_id, expiry_date, description)
   */
  upload: async (file, data = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    
    // Adicionar campos de dados ao FormData
    if (data.title) formData.append('title', data.title)
    if (data.category_id) formData.append('category_id', data.category_id)
    if (data.expiry_date) formData.append('expiry_date', data.expiry_date)
    if (data.description) formData.append('description', data.description)

    const response = await api.post('/api/documents/company', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },

  /**
   * Atualiza documento da empresa
   * @param {number} id - ID do documento
   * @param {Object} data - Dados a atualizar (title, category_id, expiry_date, description)
   */
  update: async (id, data) => {
    const response = await api.put(`/api/documents/company/${id}`, data)
    return response.data
  },

  /**
   * Deleta documento da empresa
   * @param {number} id - ID do documento
   */
  delete: async (id) => {
    const response = await api.delete(`/api/documents/company/${id}`)
    return response.data
  }
}

// ============================================
// DOCUMENTOS DO USUÁRIO (PRÓPRIOS)
// ============================================
export const myDocumentService = {
  /**
   * Lista documentos do próprio usuário
   * Inclui documentos existentes e lista de documentos faltantes (obrigatórios)
   */
  getAll: async () => {
    const response = await api.get('/api/documents/my-documents')
    return response.data
  },

  /**
   * Faz upload de documento do usuário
   * @param {File} file - Arquivo a ser enviado
   * @param {Object} data - Dados do documento (category_id, title, expiry_date)
   */
  upload: async (file, data = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    
    // Adicionar campos de dados ao FormData
    if (data.category_id) formData.append('category_id', data.category_id)
    if (data.title) formData.append('title', data.title)
    if (data.expiry_date) formData.append('expiry_date', data.expiry_date)
    if (data.force_create !== undefined) formData.append('force_create', data.force_create)

    const response = await api.post('/api/documents/my-documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },

  /**
   * Atualiza documento do usuário
   * @param {number} id - ID do documento
   * @param {Object} data - Dados a atualizar (expiry_date)
   * @param {File} file - (Opcional) Novo arquivo para substituir
   */
  update: async (id, data = {}, file = null) => {
    const formData = new FormData()
    
    // Se houver novo arquivo, adicionar ao FormData
    if (file) {
      formData.append('file', file)
    }
    
    // Adicionar campos de dados ao FormData
    if (data.expiry_date) formData.append('expiry_date', data.expiry_date)

    const response = await api.put(`/api/documents/my-documents/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },

  /**
   * Deleta documento do usuário
   * @param {number} id - ID do documento
   */
  delete: async (id) => {
    const response = await api.delete(`/api/documents/my-documents/${id}`)
    return response.data
  }
}

// ============================================
// DOCUMENTOS DOS USUÁRIOS (VISUALIZAÇÃO)
// ============================================
export const userDocumentService = {
  /**
   * Lista documentos de todos os usuários (agrupados por usuário)
   * @param {Object} filters - Filtros (access_group_id, page, limit)
   */
  getAll: async (filters = {}) => {
    const response = await api.get('/api/documents/users', { params: filters })
    return response.data
  },

  /**
   * Busca documentos de um usuário específico
   * @param {number} userId - ID do usuário
   */
  getByUserId: async (userId) => {
    const response = await api.get(`/api/documents/users/${userId}`)
    return response.data
  }
}

// ============================================
// CATEGORIAS DE DOCUMENTOS
// ============================================
export const documentCategoryService = {
  /**
   * Lista categorias de documentos
   * @param {Object} filters - Filtros (type: 'company' | 'user', active: boolean)
   */
  getAll: async (filters = {}) => {
    const response = await api.get('/api/documents/categories', { params: filters })
    return response.data
  },

  /**
   * Busca categoria por ID
   * @param {number} id - ID da categoria
   */
  getById: async (id) => {
    const response = await api.get(`/api/documents/categories/${id}`)
    return response.data
  },

  /**
   * Cria nova categoria (apenas Admin)
   * @param {Object} data - Dados da categoria (name, description, type, required, expiry_days, metadata)
   */
  create: async (data) => {
    const response = await api.post('/api/documents/categories', data)
    return response.data
  },

  /**
   * Atualiza categoria (apenas Admin)
   * @param {number} id - ID da categoria
   * @param {Object} data - Dados a atualizar
   */
  update: async (id, data) => {
    const response = await api.put(`/api/documents/categories/${id}`, data)
    return response.data
  },

  /**
   * Deleta categoria (apenas Admin)
   * @param {number} id - ID da categoria
   */
  delete: async (id) => {
    const response = await api.delete(`/api/documents/categories/${id}`)
    return response.data
  }
}

// ============================================
// EXPORT DEFAULT (Compatibilidade)
// ============================================
const documentsApiDefault = {
  dashboard: documentDashboardService,
  company: companyDocumentService,
  myDocuments: myDocumentService,
  userDocuments: userDocumentService,
  categories: documentCategoryService
}

export default documentsApiDefault
